import MenuContext, { MenuOptions } from './MenuContext'
import React from 'react'

type Menu = JSX.Element | undefined

const MenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMenu, showMenu] = React.useState<number | boolean>(false)
  const menuRef = React.useRef<Menu>(undefined)
  const optionsRef = React.useRef<MenuOptions | undefined>(undefined)
  const openedAtRef = React.useRef(0)

  const updateMenu = React.useCallback((menu: Menu = undefined, options?: MenuOptions) => {
    const prevOnClose = optionsRef.current?.onClose
    menuRef.current = menu
    optionsRef.current = menu ? options : undefined
    openedAtRef.current = Date.now()
    showMenu(menu ? openedAtRef.current : false)
    if (prevOnClose && prevOnClose !== options?.onClose) prevOnClose()
  }, [])

  React.useEffect(() => {
    if (isMenu) {
      const handleClick = (_: Event) => {
        if (optionsRef.current?.persistent) return
        if (Date.now() - openedAtRef.current > 300) {
          updateMenu()
        }
      }
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.code === 'Escape') updateMenu()
      }

      window.addEventListener('click', handleClick)
      window.addEventListener('keydown', handleKeyDown)

      return () => {
        window.removeEventListener('click', handleClick)
        window.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isMenu, updateMenu])

  return (
    <MenuContext.Provider value={updateMenu}>
      {isMenu ? menuRef.current : false}
      {children}
    </MenuContext.Provider>
  )
}

export default MenuProvider
